
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment, MenuItem, Select as MuiSelect, FormControl,
  FormControlLabel, Checkbox, Radio, RadioGroup,
} from '@mui/material';
import { FixedSizeList } from 'react-window';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LabelIcon from '@mui/icons-material/Label';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import Select from 'react-select';

import { fetchData, deleteData, activateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const ROW_HEIGHT  = 36;
const LIST_HEIGHT = 500;

// ── Column definitions per master type ───────────────────────────────────────
// flex: CSS flex shorthand applied to BOTH header cells and row cells.
// Using the same flex value on both guarantees perfect alignment.
const COLS_MAP = {
  1: [ // Account Head
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Name',           flex: '2 1 0',      align: 'left'  },
    { label: 'Searchable Name',flex: '2 1 0',      align: 'left'  },
    { label: 'CGST %',         flex: '0 0 70px',   align: 'right' },
    { label: 'SGST %',         flex: '0 0 70px',   align: 'right' },
    { label: 'HSN Code',       flex: '0 0 100px',  align: 'left'  },
    { label: 'Type',           flex: '0 0 80px',   align: 'left'  },
    { label: 'Remarks',        flex: '1 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  2: [ // Assets
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Name',           flex: '2 1 0',      align: 'left'  },
    { label: 'Searchable Name',flex: '2 1 0',      align: 'left'  },
    { label: 'Amount',         flex: '0 0 90px',   align: 'right' },
    { label: 'Depreciation',   flex: '0 0 100px',  align: 'right' },
    { label: 'CGST %',         flex: '0 0 65px',   align: 'right' },
    { label: 'SGST %',         flex: '0 0 65px',   align: 'right' },
    { label: 'HSN Code',       flex: '0 0 90px',   align: 'left'  },
    { label: 'Remarks',        flex: '1 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  3: [ // Bank
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Name',           flex: '2 1 0',      align: 'left'  },
    { label: 'Account Name',   flex: '2 1 0',      align: 'left'  },
    { label: 'Branch',         flex: '1 1 0',      align: 'left'  },
    { label: 'IFSC',           flex: '0 0 100px',  align: 'left'  },
    { label: 'Contact',        flex: '0 0 110px',  align: 'left'  },
    { label: 'Address',        flex: '2 1 0',      align: 'left'  },
    { label: 'Remarks',        flex: '1 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  4: [ // Capital
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Name',           flex: '2 1 0',      align: 'left'  },
    { label: 'Searchable Name',flex: '2 1 0',      align: 'left'  },
    { label: 'Contact',        flex: '0 0 130px',  align: 'left'  },
    { label: 'Email',          flex: '2 1 0',      align: 'left'  },
    { label: 'Address',        flex: '2 1 0',      align: 'left'  },
    { label: 'Remarks',        flex: '1 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  5: [ // Customer Or Supplier
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Invoice Type',   flex: '0 0 100px',  align: 'left'  },
    { label: 'Name',           flex: '3 1 0',      align: 'left'  },
    { label: 'Contact',        flex: '0 0 130px',  align: 'left'  },
    { label: 'Address',        flex: '3 1 0',      align: 'left'  },
    { label: 'GST No',         flex: '0 0 140px',  align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  6: [ // Others
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Balance Sheet',  flex: '2 1 0',      align: 'left'  },
    { label: 'Name',           flex: '3 1 0',      align: 'left'  },
    { label: 'Searchable Name',flex: '2 1 0',      align: 'left'  },
    { label: 'Remarks',        flex: '2 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
  7: [ // Staff
    { label: '#',              flex: '0 0 44px',   align: 'left'  },
    { label: 'Name',           flex: '2 1 0',      align: 'left'  },
    { label: 'Searchable Name',flex: '2 1 0',      align: 'left'  },
    { label: 'Contact',        flex: '0 0 130px',  align: 'left'  },
    { label: 'Email',          flex: '2 1 0',      align: 'left'  },
    { label: 'Address',        flex: '2 1 0',      align: 'left'  },
    { label: 'Remarks',        flex: '1 1 0',      align: 'left'  },
    { label: '',               flex: '0 0 76px',   align: 'right' },
  ],
};

// ── Shared row/cell styles ────────────────────────────────────────────────────
// IMPORTANT: rowBase must NOT set width — react-window injects width via style prop.
// The inner flex container fills that injected width automatically.
const rowBase = {
  display: 'flex', alignItems: 'center',
  boxSizing: 'border-box', borderBottom: `1px solid ${C.cardBorder}`,
};

function ColCell({ col, children, muted, bold }) {
  return (
    <div style={{
      flex: col.flex, minWidth: 0,
      padding: '0 8px', fontSize: '12px',
      color: muted ? C.muted : C.text, fontWeight: bold ? 600 : 400,
      textAlign: col.align, overflow: 'hidden', whiteSpace: 'nowrap',
      textOverflow: 'ellipsis', boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

// ── Memoised row per master type ──────────────────────────────────────────────
const MasterRow = React.memo(({ index, style, data: listData }) => {
  const { items, onDelete, hasEdit, hasDelete, masterTypeId } = listData;
  const item = items[index];
  const isEven = index % 2 === 0;
  const cols = COLS_MAP[masterTypeId] || COLS_MAP[1];

  const renderCells = () => {
    switch (masterTypeId) {
      case 1: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]} bold>{item.name}</ColCell>
        <ColCell col={cols[2]}>{item.searchableName}</ColCell>
        <ColCell col={cols[3]}>{item.cgstRate}%</ColCell>
        <ColCell col={cols[4]}>{item.sgstRate}%</ColCell>
        <ColCell col={cols[5]}>{item.hsnCode}</ColCell>
        <ColCell col={cols[6]}>
          <span style={{ color: item.isExpenseOrIncome ? '#2563eb' : '#b45309', fontSize: '11px', fontWeight: 600 }}>
            {item.isExpenseOrIncome ? 'Income' : 'Expense'}
          </span>
        </ColCell>
        <ColCell col={cols[7]}>{item.remarks}</ColCell>
      </>);
      case 2: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]} bold>{item.name}</ColCell>
        <ColCell col={cols[2]}>{item.searchableName}</ColCell>
        <ColCell col={cols[3]}>{item.amount}</ColCell>
        <ColCell col={cols[4]}>{item.depreciation}</ColCell>
        <ColCell col={cols[5]}>{item.cgstRate}%</ColCell>
        <ColCell col={cols[6]}>{item.sgstRate}%</ColCell>
        <ColCell col={cols[7]}>{item.hsnCode}</ColCell>
        <ColCell col={cols[8]}>{item.remarks}</ColCell>
      </>);
      case 3: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]} bold>{item.name}</ColCell>
        <ColCell col={cols[2]}>{item.accountName}</ColCell>
        <ColCell col={cols[3]}>{item.branchName}</ColCell>
        <ColCell col={cols[4]}>{item.ifscCode}</ColCell>
        <ColCell col={cols[5]}>{item.contactNumber}</ColCell>
        <ColCell col={cols[6]}>{item.addressLine1}</ColCell>
        <ColCell col={cols[7]}>{item.remarks}</ColCell>
      </>);
      case 4: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]} bold>{item.name}</ColCell>
        <ColCell col={cols[2]}>{item.searchableName}</ColCell>
        <ColCell col={cols[3]}>{item.contactNumber1}{item.contactNumber2 ? `, ${item.contactNumber2}` : ''}</ColCell>
        <ColCell col={cols[4]}>{item.email}</ColCell>
        <ColCell col={cols[5]}>{item.addressLine1}</ColCell>
        <ColCell col={cols[6]}>{item.remarks}</ColCell>
      </>);
      case 5: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]}>{item.invoicetypes}</ColCell>
        <ColCell col={cols[2]} bold>{item.name}</ColCell>
        <ColCell col={cols[3]}>{item.contactNumber1}{item.contactNumber2 ? `, ${item.contactNumber2}` : ''}</ColCell>
        <ColCell col={cols[4]}>{item.addressLine1}</ColCell>
        <ColCell col={cols[5]}>{item.gstNumber}</ColCell>
      </>);
      case 6: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]}>{item.balancesheet}</ColCell>
        <ColCell col={cols[2]} bold>{item.name}</ColCell>
        <ColCell col={cols[3]}>{item.searchableName}</ColCell>
        <ColCell col={cols[4]}>{item.remarks}</ColCell>
      </>);
      case 7: return (<>
        <ColCell col={cols[0]} muted>{index + 1}</ColCell>
        <ColCell col={cols[1]} bold>{item.name}</ColCell>
        <ColCell col={cols[2]}>{item.searchableName}</ColCell>
        <ColCell col={cols[3]}>{item.contactNumber1}{item.contactNumber2 ? `, ${item.contactNumber2}` : ''}</ColCell>
        <ColCell col={cols[4]}>{item.email}</ColCell>
        <ColCell col={cols[5]}>{item.addressLine1}</ColCell>
        <ColCell col={cols[6]}>{item.remarks}</ColCell>
      </>);
      default: return null;
    }
  };

  const actionCol = cols[cols.length - 1];

  return (
    <div
      style={{
        ...style,
        ...rowBase,
        backgroundColor: isEven ? C.card : '#f7fdfc',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.accentDim; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = isEven ? C.card : '#f7fdfc'; }}
    >
      {renderCells()}
      <ColCell col={actionCol}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {hasEdit && (
            <Tooltip title="Edit" arrow>
              <IconButton size="small" component={Link}
                to={`/CapitalEdit/${masterTypeId === 6 ? item.id : item.masterId}/${item.masterTypeId}`}
                sx={{ color: '#7c3aed', p: '3px', '&:hover': { bgcolor: 'rgba(124,58,237,0.08)' } }}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {hasDelete && (
            <Tooltip title={item.isActive ? 'Delete' : 'Activate'} arrow>
              <IconButton size="small"
                onClick={() => item.isActive ? onDelete(item.masterId) : null}
                sx={{ color: item.isActive ? C.danger : '#22c55e', p: '3px', '&:hover': { bgcolor: item.isActive ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)' } }}>
                {item.isActive ? <DeleteIcon sx={{ fontSize: 15 }} /> : <CheckCircleIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
        </div>
      </ColCell>
    </div>
  );
});

// ── react-select styles ───────────────────────────────────────────────────────
const rsStyles = {
  control: (base, state) => ({
    ...base, minHeight: '30px', height: '30px', fontSize: '12px',
    borderRadius: '7px', backgroundColor: '#fafafa',
    borderColor: state.isFocused ? C.accent : C.cardBorder,
    boxShadow: state.isFocused ? `0 0 0 1.5px ${C.accent}` : 'none',
    '&:hover': { borderColor: C.accentBorder },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, fontSize: '12px', margin: 0, padding: 0 }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  option: (base, state) => ({
    ...base, fontSize: '12px',
    backgroundColor: state.isSelected ? C.accent : state.isFocused ? C.accentDim : '#fff',
    color: state.isSelected ? '#fff' : C.text,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ── Reusable form field ───────────────────────────────────────────────────────
const Field = ({ label, name, value, onChange, placeholder, type = 'text',
  required, align, maxLength, disabled }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small" name={name} value={value ?? ''} onChange={onChange}
      placeholder={placeholder || label} type={type} required={required} disabled={disabled}
      inputProps={{ maxLength, style: { fontSize: '12px', textAlign: align || 'left', padding: '5px 8px' } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px', bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

const SelectField = ({ label, required, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    {children}
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
function MasterIndex() {
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();

  const hasCreateMasterPermission = permissions.includes('Create Master');
  const hasEditMasterPermission   = permissions.includes('Edit Master');
  const hasDeleteMasterPermission = permissions.includes('Delete Master');

  const [data, setData]                     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const containerRef = useRef(null);

  // Delete dialog
  const [deleteId, setDeleteId]     = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Registration dialog
  const [regOpen, setRegOpen] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Master type filter
  const [masterTypeOptions, setMasterTypeOptions] = useState([]);
  const [selectedMasterType, setSelectedMasterType] = useState(null);
  const [selectedMasterTypeId, setSelectedMasterTypeId] = useState(null);

  // Dropdowns for form
  const [balanceSheetOptions, setBalanceSheetOptions] = useState([]);
  const [invoiceTypeOptions, setInvoiceTypeOptions]   = useState([]);

  // Form state - common
  const [name, setName]                   = useState('');
  const [searchableName, setSearchableName] = useState('');
  const [remarks, setRemarks]             = useState('');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [isPayable, setIsPayable]         = useState(true);
  const [selectedBalanceSheet, setSelectedBalanceSheet] = useState(null);
  const [selectedFormMasterType, setSelectedFormMasterType] = useState(null);

  // Capital
  const [contactNumber1, setContactNumber1] = useState('');
  const [contactNumber2, setContactNumber2] = useState('');
  const [email, setEmail]                   = useState('');
  const [addressLine1, setAddressLine1]     = useState('');
  const [addressLine2, setAddressLine2]     = useState('');

  // Customer/Supplier
  const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
  const [gstNumber, setGstNumber]         = useState('');
  const [panNumber, setPanNumber]         = useState('');
  const [customerType, setCustomerType]   = useState('1');
  const [location, setLocation]           = useState('');
  const [pincode, setPincode]             = useState('');
  const [creditDays, setCreditDays]       = useState('0');
  const [creditLimit, setCreditLimit]     = useState('0');
  const [deliveryAddr1, setDeliveryAddr1] = useState('');
  const [deliveryAddr2, setDeliveryAddr2] = useState('');

  // Bank
  const [accountName, setAccountName] = useState('');
  const [branchName, setBranchName]   = useState('');
  const [ifscCode, setIfscCode]       = useState('');

  // Staff
  const [isLoginNeeded, setIsLoginNeeded] = useState(false);
  const [firstName, setFirstName]         = useState('');
  const [lastName, setLastName]           = useState('');
  const [userName, setUserName]           = useState('');

  // Account Head
  const [cgstRate, setCgstRate]           = useState('');
  const [sgstRate, setSgstRate]           = useState('');
  const [hsnCode, setHsnCode]             = useState('');
  const [isExpenseOrIncome, setIsExpenseOrIncome] = useState(true);

  // Assets
  const [amount, setAmount]               = useState('');
  const [depreciation, setDepreciation]   = useState('');

  useEffect(() => {
    const generatedUserName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
    setUserName(generatedUserName.slice(0, 12));
  }, [firstName, lastName]);

  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(val), 200);
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    loadDropdowns();
  }, [isAuthenticated, companyId, token]);

  const loadDropdowns = async () => {
    if (!token || !companyId) return;
    try {
      const [mtRes, bsRes, itRes] = await Promise.all([
        fetchData('MasterApi', `/MasterTypeData/${companyId}`, token),
        fetchData('MasterApi', `/BalanceSheetData/${companyId}`, token),
        fetchData('MasterApi', `/InvoiceTypeData/${companyId}`, token),
      ]);
      if (mtRes.responseCode === '1111')
        setMasterTypeOptions(mtRes.response_data.map(m => ({ value: m.Id, label: m.Name })));
      if (bsRes.responseCode === '1111')
        setBalanceSheetOptions(bsRes.response_data.map(b => ({ value: b.Id, label: b.Name })));
      if (itRes.responseCode === '1111')
        setInvoiceTypeOptions(itRes.response_data.map(i => ({ value: i.Id, label: i.Name })));
    } catch (err) {
      console.error('Error loading dropdowns:', err);
    }
  };

  const handleMasterTypeChange = async (option) => {
    setSelectedMasterType(option);
    setSelectedMasterTypeId(option.value);
    setData([]);
    setLoading(true);
    try {
      let res;
      switch (option.value) {
        case 1: res = await fetchData('AccountHeadApi', `/${companyId}/${option.value}`, token); break;
        case 2: res = await fetchData('AssetsApi', `/${companyId}/${option.value}`, token); break;
        case 3: res = await fetchData('BankApi', `/${companyId}/${option.value}`, token); break;
        case 4: res = await fetchData('CapitalApi', `/${companyId}/${option.value}`, token); break;
        case 5: res = await fetchData('SupplierAndCustomerApi', `/${companyId}/${option.value}`, token); break;
        case 6: res = await fetchData('MasterApi', `/${companyId}/${option.value}`, token); break;
        case 7: res = await fetchData('StaffApi', `/${companyId}/${option.value}`, token); break;
        default: res = { responseCode: '0000' };
      }
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) {
      console.error('Error fetching master data:', err);
      if (err.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return data;
    const q = debouncedQuery.toLowerCase();
    return data.filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.searchableName?.toLowerCase().includes(q)
    );
  }, [data, debouncedQuery]);

  const cols = COLS_MAP[selectedMasterTypeId] || [];

  const itemData = useMemo(() => ({
    items: filteredData,
    onDelete: (id) => { setDeleteId(id); setDeleteOpen(true); },
    hasEdit: hasEditMasterPermission,
    hasDelete: hasDeleteMasterPermission,
    masterTypeId: selectedMasterTypeId,
  }), [filteredData, hasEditMasterPermission, hasDeleteMasterPermission, selectedMasterTypeId]);

  const closeDelete = useCallback(() => { setDeleteId(null); setDeleteOpen(false); }, []);
  const handleDelete = async () => {
    try {
      await deleteData('AccountHeadApi', `${deleteId}`, token);
      closeDelete();
      if (selectedMasterType) handleMasterTypeChange(selectedMasterType);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const openReg = () => {
    setError(''); setSuccess('');
    setName(''); setSearchableName(''); setRemarks(''); setOpeningBalance('0'); setIsPayable(true);
    setSelectedBalanceSheet(null); setSelectedFormMasterType(null);
    setContactNumber1(''); setContactNumber2(''); setEmail(''); setAddressLine1(''); setAddressLine2('');
    setSelectedInvoiceType(null); setGstNumber(''); setPanNumber(''); setCustomerType('1');
    setLocation(''); setPincode(''); setCreditDays('0'); setCreditLimit('0');
    setDeliveryAddr1(''); setDeliveryAddr2('');
    setAccountName(''); setBranchName(''); setIfscCode('');
    setIsLoginNeeded(false); setFirstName(''); setLastName(''); setUserName('');
    setCgstRate(''); setSgstRate(''); setHsnCode(''); setIsExpenseOrIncome(true);
    setAmount(''); setDepreciation('');
    setRegOpen(true);
  };
  const closeReg = () => { setRegOpen(false); setError(''); setSuccess(''); };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!selectedFormMasterType?.value) { setError('Please select Master Type.'); return; }
    if (!selectedBalanceSheet?.value) { setError('Please select Balance Sheet Head.'); return; }

    const formMasterLabel = selectedFormMasterType.label;
    setSaving(true);
    setError('');
    try {
      if (formMasterLabel === 'Staff') {
        await postData('StaffApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          ContactNumber1: contactNumber1, ContactNumber2: contactNumber2,
          Email: email, AddressLine1: addressLine1, AddressLine2: addressLine2,
          IsLoginNeeded: isLoginNeeded, FirstName: firstName, LastName: lastName, UserName: userName,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else if (formMasterLabel === 'Assets') {
        await postData('AssetsApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          Amount: amount, Depreciation: depreciation,
          CGSTRate: cgstRate, SGSTRate: sgstRate, HSNCode: hsnCode,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else if (formMasterLabel === 'Bank') {
        if (!accountName.trim()) { setError('Account Name is required.'); setSaving(false); return; }
        if (!branchName.trim())  { setError('Branch Name is required.'); setSaving(false); return; }
        await postData('BankApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          AccountName: accountName, BranchName: branchName, IFSCCode: ifscCode,
          AddressLine1: addressLine1, AddressLine2: addressLine2, ContactNumber: contactNumber1,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else if (formMasterLabel === 'Capital') {
        await postData('CapitalApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          ContactNumber1: contactNumber1, ContactNumber2: contactNumber2,
          Email: email, AddressLine1: addressLine1, AddressLine2: addressLine2,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else if (formMasterLabel === 'Customer Or Supplier') {
        if (!selectedInvoiceType?.value) { setError('Please select Invoice Type.'); setSaving(false); return; }
        if (!contactNumber1) { setError('Contact Number 1 is required.'); setSaving(false); return; }
        if (!addressLine1)   { setError('Address Line 1 is required.'); setSaving(false); return; }
        await postData('SupplierAndCustomerApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          InvoiceTypeId: selectedInvoiceType.value, SalesExecutiveId: null,
          ContactNumber1: contactNumber1, ContactNumber2: contactNumber2,
          Email: email, AddressLine1: addressLine1, AddressLine2: addressLine2,
          DeliveryAddressLine1: deliveryAddr1, DeliveryAddressLine2: deliveryAddr2,
          Location: location, PinCode: pincode, GSTNumber: gstNumber, PanNumber: panNumber,
          CreditDays: creditDays, CreditLimit: creditLimit, CustomerType: customerType,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else if (formMasterLabel === 'Account Head') {
        await postData('AccountHeadApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          CGSTRate: cgstRate, SGSTRate: sgstRate, HSNCode: hsnCode,
          IsExpenseOrIncome: isExpenseOrIncome,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      } else {
        await postData('MasterApi', {
          CompanyId: companyId, MasterTypeId: selectedFormMasterType.value,
          BalanceSheetHeadId: selectedBalanceSheet.value,
          Name: name, SearchableName: searchableName, Remarks: remarks,
          IsPayableOrReceivable: isPayable, OpeningBalance: openingBalance,
          CreatedBy: userId, CreatedDate: new Date(),
        }, token);
      }
      setSuccess(`${formMasterLabel} created successfully!`);
      setTimeout(() => {
        closeReg();
        if (selectedMasterType) handleMasterTypeChange(selectedMasterType);
      }, 1200);
    } catch (err) {
      console.error('Error saving master:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Button styles
  const btnPrimary = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.accent, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.accentDark }, '&:disabled': { bgcolor: C.accentBorder },
  };
  const btnSecondary = {
    fontSize: '11px', fontWeight: 600, textTransform: 'none',
    color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: '7px', px: 1.4, py: 0.5,
    '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
  };
  const btnDanger = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.danger, color: '#fff', borderRadius: '7px', px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.dangerDark },
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <AccountBalanceIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Master Registration
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Master Type filter */}
          <Box sx={{ width: 180 }}>
            <Select
              value={selectedMasterType}
              onChange={handleMasterTypeChange}
              options={masterTypeOptions}
              placeholder="Select Master Type…"
              isSearchable
              styles={rsStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </Box>

          <TextField
            size="small" placeholder="Search by name…"
            value={searchQuery} onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: C.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', fontSize: '12px', height: '28px', bgcolor: C.card,
                '& fieldset': { borderColor: C.cardBorder },
                '&:hover fieldset': { borderColor: C.accentBorder },
                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
              },
            }}
          />

          {hasCreateMasterPermission && (
            <Button onClick={openReg} size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '11px', fontWeight: 700, textTransform: 'none',
                bgcolor: C.accent, color: '#fff', borderRadius: '7px',
                px: 1.4, py: 0.4, boxShadow: C.shadow,
                '&:hover': { bgcolor: C.accentDark },
              }}>
              Registration
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{
        bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
        border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
      }}>
        {/* Card title bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
          bgcolor: `${C.accent}0d`,
        }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            {selectedMasterType ? `${selectedMasterType.label} List` : 'Master List'}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {selectedMasterTypeId
                ? `${filteredData.length} of ${data.length} record${data.length !== 1 ? 's' : ''}`
                : 'Select a master type to view records'}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : !selectedMasterTypeId ? (
          <Box sx={{ textAlign: 'center', py: 6, color: C.muted, fontSize: '12px' }}>
            <AccountBalanceIcon sx={{ fontSize: 32, color: C.accentBorder, mb: 1 }} />
            <Typography sx={{ fontSize: '12px', color: C.muted }}>
              Select a master type from the dropdown above to view records
            </Typography>
          </Box>
        ) : (
          <div ref={containerRef} style={{ width: '100%' }}>
            {/* Column header — width: 100% matches FixedSizeList's 100% so flex columns align */}
            <div style={{
              ...rowBase,
              width: '100%',
              backgroundColor: '#f8fafa',
              borderBottom: `2px solid ${C.cardBorder}`,
            }}>
              {cols.map((col, i) => (
                <div key={i} style={{
                  flex: col.flex, minWidth: 0,
                  padding: '7px 8px', fontSize: '10px', fontWeight: 700, color: C.muted,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  textAlign: col.align, boxSizing: 'border-box',
                }}>
                  {col.label}
                </div>
              ))}
            </div>

            {filteredData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: C.muted, fontSize: '12px' }}>
                No records found
              </Box>
            ) : (
              <FixedSizeList
                height={Math.min(LIST_HEIGHT, filteredData.length * ROW_HEIGHT)}
                itemCount={filteredData.length}
                itemSize={ROW_HEIGHT}
                width="100%"
                itemData={itemData}
                overscanCount={8}
              >
                {MasterRow}
              </FixedSizeList>
            )}
          </div>
        )}
      </Box>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
          Delete Record?
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
            Are you sure you want to delete this record? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger} startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Registration Dialog ── */}
      <Dialog open={regOpen} onClose={closeReg} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '13px', fontWeight: 800, color: C.text, pb: 0.5,
          borderBottom: `1px solid ${C.cardBorder}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <PersonIcon sx={{ fontSize: 15, color: C.accent }} />
            Master Registration
          </Box>
          <IconButton size="small" onClick={closeReg} sx={{ color: C.muted }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: '14px !important' }}>
          {error && (
            <Box sx={{ mb: 1.5, px: '10px', py: '6px', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px' }}>
              <Typography sx={{ fontSize: '11px', color: C.danger }}>{error}</Typography>
            </Box>
          )}
          {success && (
            <Box sx={{ mb: 1.5, px: '10px', py: '6px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px' }}>
              <Typography sx={{ fontSize: '11px', color: '#16a34a' }}>{success}</Typography>
            </Box>
          )}

          {/* ── Common fields ── */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
            <Box sx={{ flex: '1 1 180px' }}>
              <SelectField label="Balance Sheet" required>
                <Select value={selectedBalanceSheet} onChange={setSelectedBalanceSheet}
                  options={balanceSheetOptions} placeholder="Select…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed" />
              </SelectField>
            </Box>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Name" value={name} onChange={e => setName(e.target.value)} required />
            </Box>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Searchable Name" value={searchableName} onChange={e => setSearchableName(e.target.value)} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
            <Box sx={{ flex: '2 1 200px' }}>
              <Field label="Remarks" value={remarks} onChange={e => setRemarks(e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <SelectField label="Master Type" required>
                <Select value={selectedFormMasterType} onChange={setSelectedFormMasterType}
                  options={masterTypeOptions} placeholder="Select…"
                  isSearchable styles={rsStyles}
                  menuPortalTarget={document.body} menuPosition="fixed" />
              </SelectField>
            </Box>
            <Box sx={{ flex: '1 1 120px' }}>
              <Field label="Opening Balance" value={openingBalance}
                onChange={e => setOpeningBalance(e.target.value)} type="number" align="right" />
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
              <FormControlLabel
                control={<Checkbox checked={isPayable} onChange={e => setIsPayable(e.target.checked)}
                  size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                label={<Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'brown' }}>Is Payable</Typography>}
              />
            </Box>
          </Box>

          {/* ── Capital fields ── */}
          {selectedFormMasterType?.label === 'Capital' && (<>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" value={contactNumber1} onChange={e => setContactNumber1(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" value={contactNumber2} onChange={e => setContactNumber2(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" value={email} onChange={e => setEmail(e.target.value)} maxLength={40} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} maxLength={100} />
              </Box>
            </Box>
          </>)}

          {/* ── Customer Or Supplier fields ── */}
          {selectedFormMasterType?.label === 'Customer Or Supplier' && (<>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 160px' }}>
                <SelectField label="Invoice Type" required>
                  <Select value={selectedInvoiceType} onChange={setSelectedInvoiceType}
                    options={invoiceTypeOptions} placeholder="Select…"
                    isSearchable styles={rsStyles}
                    menuPortalTarget={document.body} menuPosition="fixed" />
                </SelectField>
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="GST No" value={gstNumber} onChange={e => setGstNumber(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="PAN No" value={panNumber} onChange={e => setPanNumber(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
                <RadioGroup row value={customerType} onChange={e => setCustomerType(e.target.value)}>
                  <FormControlLabel value="1" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#034C53' }}>Customer</Typography>} />
                  <FormControlLabel value="2" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#328E6E' }}>Supplier</Typography>} />
                  <FormControlLabel value="3" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#3A0519' }}>Both</Typography>} />
                </RadioGroup>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" required value={contactNumber1} onChange={e => setContactNumber1(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" value={contactNumber2} onChange={e => setContactNumber2(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" value={email} onChange={e => setEmail(e.target.value)} maxLength={40} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" required value={addressLine1} onChange={e => setAddressLine1(e.target.value)} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} maxLength={100} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Delivery Address Line 1" value={deliveryAddr1} onChange={e => setDeliveryAddr1(e.target.value)} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Delivery Address Line 2" value={deliveryAddr2} onChange={e => setDeliveryAddr2(e.target.value)} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '2 1 160px' }}>
                <Field label="Location" value={location} onChange={e => setLocation(e.target.value)} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="PIN Code" value={pincode} onChange={e => setPincode(e.target.value)} maxLength={20} />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="Credit Days" value={creditDays} onChange={e => setCreditDays(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 100px' }}>
                <Field label="Credit Limit" value={creditLimit} onChange={e => setCreditLimit(e.target.value)} type="number" align="right" />
              </Box>
            </Box>
          </>)}

          {/* ── Bank fields ── */}
          {selectedFormMasterType?.label === 'Bank' && (<>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 160px' }}>
                <Field label="Account Name" required value={accountName} onChange={e => setAccountName(e.target.value)} />
              </Box>
              <Box sx={{ flex: '1 1 160px' }}>
                <Field label="Branch Name" required value={branchName} onChange={e => setBranchName(e.target.value)} />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="IFSC Code" value={ifscCode} onChange={e => setIfscCode(e.target.value)} />
              </Box>
              <Box sx={{ flex: '1 1 130px' }}>
                <Field label="Contact No" value={contactNumber1} onChange={e => setContactNumber1(e.target.value)} maxLength={15} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} maxLength={100} />
              </Box>
            </Box>
          </>)}

          {/* ── Staff fields ── */}
          {selectedFormMasterType?.label === 'Staff' && (<>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 1" value={contactNumber1} onChange={e => setContactNumber1(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '1 1 140px' }}>
                <Field label="Contact No 2" value={contactNumber2} onChange={e => setContactNumber2(e.target.value)} maxLength={15} />
              </Box>
              <Box sx={{ flex: '2 1 200px' }}>
                <Field label="Email" value={email} onChange={e => setEmail(e.target.value)} maxLength={40} />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
                <FormControlLabel
                  control={<Checkbox checked={isLoginNeeded} onChange={e => setIsLoginNeeded(e.target.checked)}
                    size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                  label={<Typography sx={{ fontSize: '11px', fontWeight: 600, color: C.text }}>Is Login Needed?</Typography>}
                />
              </Box>
            </Box>
            {isLoginNeeded && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
                <Box sx={{ flex: '1 1 140px' }}>
                  <Field label="First Name" required value={firstName} onChange={e => setFirstName(e.target.value)} maxLength={30} />
                </Box>
                <Box sx={{ flex: '1 1 140px' }}>
                  <Field label="Last Name" required value={lastName} onChange={e => setLastName(e.target.value)} maxLength={30} />
                </Box>
                <Box sx={{ flex: '1 1 140px' }}>
                  <Field label="User Name" required value={userName} onChange={e => setUserName(e.target.value)} disabled />
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 1" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} maxLength={100} />
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Field label="Address Line 2" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} maxLength={100} />
              </Box>
            </Box>
          </>)}

          {/* ── Account Head fields ── */}
          {selectedFormMasterType?.label === 'Account Head' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="CGST Rate" value={cgstRate} onChange={e => setCgstRate(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="SGST Rate" value={sgstRate} onChange={e => setSgstRate(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="HSN Code" value={hsnCode} onChange={e => setHsnCode(e.target.value)} />
              </Box>
              <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', pb: '2px' }}>
                <RadioGroup row value={String(isExpenseOrIncome)} onChange={e => setIsExpenseOrIncome(e.target.value === 'true')}>
                  <FormControlLabel value="true" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: '#2563eb' }}>Income</Typography>} />
                  <FormControlLabel value="false" control={<Radio size="small" sx={{ color: C.accent, '&.Mui-checked': { color: C.accent } }} />}
                    label={<Typography sx={{ fontSize: '11px', color: 'brown' }}>Expense</Typography>} />
                </RadioGroup>
              </Box>
            </Box>
          )}

          {/* ── Assets fields ── */}
          {selectedFormMasterType?.label === 'Assets' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="Amount" value={amount} onChange={e => setAmount(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="Depreciation" value={depreciation} onChange={e => setDepreciation(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="CGST Rate" value={cgstRate} onChange={e => setCgstRate(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 110px' }}>
                <Field label="SGST Rate" value={sgstRate} onChange={e => setSgstRate(e.target.value)} type="number" align="right" />
              </Box>
              <Box sx={{ flex: '1 1 120px' }}>
                <Field label="HSN Code" value={hsnCode} onChange={e => setHsnCode(e.target.value)} />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeReg} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} sx={btnPrimary}
            startIcon={saving ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <AddIcon sx={{ fontSize: 13 }} />}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default MasterIndex;

